import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  List,
  ListItemButton,
  ListItemText,
  CircularProgress,
} from '@mui/material';
import { Mail } from '@mui/icons-material';
import { praAPI } from '../services/api';

const BRAND_HEADING = { fontFamily: 'Georgia, serif', color: '#00075e' };

/**
 * Single, app-wide "choose a sender account and send" flow.
 *
 * Both Create Request and Request Detail render this dialog with a requestId;
 * it fetches the active sender accounts, lets the user pick one, sends, and
 * reports the updated request back via `onSent`. This is the ONE send path —
 * no per-component send logic should be duplicated.
 *
 * Props:
 *   open      boolean — whether the dialog is visible
 *   requestId number  — the PRRequest id to send (must be created/saved first)
 *   onClose   () => void
 *   onSent    (request) => void — called with the updated request on success
 */
export default function SenderAccountSendDialog({ open, requestId, onClose, onSent }) {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sendingId, setSendingId] = useState(null);
  const [error, setError] = useState('');

  // Load active sender accounts every time the dialog opens.
  useEffect(() => {
    if (!open) return;
    setError('');
    setSendingId(null);
    setLoading(true);
    praAPI
      .getSenderAccounts()
      .then((res) => {
        const active = (res.data || []).filter((a) => a.is_active);
        setAccounts(active);
        if (active.length === 0) {
          setError('No active sender accounts available. Ask an admin to add a Gmail sender account.');
        }
      })
      .catch(() => setError('Failed to fetch sender accounts.'))
      .finally(() => setLoading(false));
  }, [open]);

  const handleSelect = async (accountId) => {
    setError('');
    setSendingId(accountId);
    try {
      const response = await praAPI.sendRequest(requestId, accountId);
      if (onSent) onSent(response.data);
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send request');
    } finally {
      setSendingId(null);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={BRAND_HEADING}>Select Sender Account</DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mb: 2 }}>
          Choose which Gmail account to send this request from.
        </Alert>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {loading ? (
          <CircularProgress />
        ) : (
          <List>
            {accounts.map((account) => (
              <ListItemButton
                key={account.id}
                onClick={() => handleSelect(account.id)}
                disabled={sendingId !== null}
              >
                <Mail sx={{ mr: 2, color: '#2a52be' }} />
                <ListItemText
                  primary={account.email_address}
                  secondary={account.account_name || 'No name'}
                />
                {sendingId === account.id && <CircularProgress size={20} sx={{ ml: 2 }} />}
              </ListItemButton>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={sendingId !== null}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
}
